import { Trash, Warning } from "@phosphor-icons/react";
import { ProductLine } from "./JubelioPicker";
import { btnGhost, fmtIDR, fmtNum, inp, toNum } from "./util";

function Match({ it, k, onChange, onPick }) {
    if (it.jubelio) {
        return (
            <div className="border border-emerald-200 bg-emerald-50/50 rounded-sm p-2 space-y-1" data-testid={`item-${k}-matched`}>
                <div className="text-[11px] font-semibold text-emerald-800 tracking-wide">Matched{it.matchState === "CONFIRMED" || it.matchState === "SAVED" ? " · Confirmed" : ""}</div>
                <ProductLine p={it.jubelio} testid={`item-${k}-product`} />
                <div className="flex gap-2">
                    <button type="button" className="text-xs underline" onClick={onPick} data-testid={`item-${k}-change-product`}>Change</button>
                    <button type="button" className="text-xs underline text-neutral-500" onClick={() => onChange({ jubelio: null, matchState: "NONE" })} data-testid={`item-${k}-unlink-product`}>Unlink</button>
                </div>
            </div>
        );
    }
    return (
        <div className="space-y-1">
            {(it.suggestions || []).map((s) => (
                <div key={s.item_code} className="border border-amber-200 bg-amber-50/60 rounded-sm p-2 space-y-1" data-testid={`item-${k}-suggestion-${s.item_code}`}>
                    <div className="text-[11px] font-semibold text-amber-800">Possible match</div>
                    <ProductLine p={s} testid={`item-${k}-suggestion-product-${s.item_code}`} />
                    <div className="text-[11px] text-neutral-500">{s.reason}</div>
                    <button type="button" className={btnGhost} onClick={() => onChange({ jubelio: { item_id: s.item_id, item_code: s.item_code, name: s.name, thumbnail: s.thumbnail, catalog_image: s.catalog_image, stock: s.stock }, matchState: "OWNER", suggestions: [] })} data-testid={`item-${k}-confirm-${s.item_code}`}>Confirm</button>
                </div>
            ))}
            <div className="flex items-center justify-between gap-2 border border-rose-200 bg-rose-50/40 rounded-sm p-2" data-testid={`item-${k}-unmatched`}>
                <span className="text-[11px] font-semibold text-rose-700">Unmatched</span>
                <button type="button" className="text-xs underline" onClick={onPick} data-testid={`item-${k}-search-jubelio`}>Find product</button>
            </div>
        </div>
    );
}

export function ItemRow({ it, idx, rate, onChange, onRemove, onPick }) {
    const k = idx;
    const set = (f) => (e) => {
        const patch = { [f]: e.target.value };
        if ((f === "qty" || f === "unit_price") && !it.lineManual) {
            const q = toNum(f === "qty" ? e.target.value : it.qty), p = toNum(f === "unit_price" ? e.target.value : it.unit_price);
            patch.line_amount = q != null && p != null ? String(Math.round(q * p * 10000) / 10000) : it.line_amount;
        }
        if (f === "line_amount") patch.lineManual = true;
        if (f === "supplier_sku") Object.assign(patch, { suggestions: [], jubelio: null, matchState: "NONE" });
        onChange(patch);
    };
    const q = toNum(it.qty), p = toNum(it.unit_price), l = toNum(it.line_amount);
    const mismatch = q != null && p != null && l != null && Math.abs(q * p - l) > 0.01;
    return (
        <div className={`border rounded-sm p-3 space-y-2 bg-white ${it.review ? "border-amber-300" : "border-mist"}`} data-testid={`invoice-item-${k}`}>
            <div className="flex items-center justify-between gap-2 text-xs">
                <span className="text-neutral-500">#{idx + 1}{it.review && <span className="ml-2 text-amber-700 inline-flex items-center gap-1" data-testid={`item-${k}-review-flag`}><Warning size={12} /> Review{it.note ? `: ${it.note}` : ""}</span>}</span>
                <span className="flex gap-3">
                    {it.review && <button type="button" className="underline" onClick={() => onChange({ review: false })} data-testid={`item-${k}-mark-ok`}>Mark checked</button>}
                    <button type="button" className="text-rose-700" onClick={onRemove} aria-label="Remove row" data-testid={`item-${k}-remove`}><Trash size={16} /></button>
                </span>
            </div>
            <input className={`${inp} font-mono font-semibold`} placeholder="Supplier SKU / Barcode" value={it.supplier_sku} onChange={set("supplier_sku")} data-testid={`item-${k}-sku`} />
            {it.source === "OCR" && (it.line_no || it.raw_line) && (
                <details className="text-[10px] text-neutral-400" data-testid={`item-${k}-ocr-line`}>
                    <summary className="cursor-pointer select-none">AI read · Line {it.line_no}</summary>
                    {it.raw_line && <div className="font-mono break-words mt-0.5">{it.raw_line}</div>}
                </details>
            )}
            <input className={inp} placeholder="Product (optional)" value={it.description} onChange={set("description")} data-testid={`item-${k}-description`} />
            <div className="grid grid-cols-3 gap-2">
                <label className="text-[10px] text-neutral-400 min-w-0">Qty<input className={inp} inputMode="decimal" value={it.qty} onChange={set("qty")} data-testid={`item-${k}-qty`} /></label>
                <label className="text-[10px] text-neutral-400 min-w-0">Unit Price<input className={inp} inputMode="decimal" value={it.unit_price} onChange={set("unit_price")} data-testid={`item-${k}-price`} /></label>
                <label className="text-[10px] text-neutral-400 min-w-0">Total<input className={`${inp} ${mismatch ? "border-amber-400" : ""}`} inputMode="decimal" value={it.line_amount} onChange={set("line_amount")} data-testid={`item-${k}-line`} /></label>
            </div>
            <div className="text-xs text-neutral-500 flex flex-wrap gap-x-3" data-testid={`item-${k}-base-cost`}>
                <span>Cost / pc: <b className="text-ink">{fmtIDR(p != null ? Math.round(p * rate) : null)}</b></span>
                <span>Total Cost: {fmtIDR(l != null ? Math.round(l * rate) : null)}</span>
                {mismatch && <span className="text-amber-700">Qty × price = {fmtNum(q * p)}</span>}
            </div>
            <Match it={it} k={k} onChange={onChange} onPick={onPick} />
        </div>
    );
}
